import type { TFunction } from 'i18next'
import {
    isAdditionalPaymentCheckoutFlow,
    type CheckoutFlow,
} from '@/checkout-paths'

import {
    getCheckoutSuccessAction,
    isRichCheckoutPaymentStatus,
} from '../summary-utils'

export type CheckoutSuccessAction = {
    actionHref: string
    actionLabel: string
}

export type CheckoutSuccessStateVariant = 'failed' | 'pending' | 'successful'

export type CheckoutSuccessStateContent = {
    variant: CheckoutSuccessStateVariant
    title: string
    text: string
    actionHref: string
    actionLabel: string
}

function getAdditionalPaymentTranslationKeyPrefix(checkoutFlow: CheckoutFlow): string {
    return checkoutFlow === 'change_unit'
        ? 'success.changeUnit'
        : 'success.prolongation'
}

export function shouldUseRichCheckoutSuccessLayout(
    paymentStatus: string | null | undefined,
): boolean {
    return typeof paymentStatus === 'string' && isRichCheckoutPaymentStatus(paymentStatus)
}

export function getFallbackCheckoutSuccessAction({
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
    t,
}: {
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
    t: TFunction
}): CheckoutSuccessAction {
    if (isAdditionalPaymentCheckoutFlow(checkoutFlow) && returnTo) {
        return {
            actionHref: returnTo,
            actionLabel: t(`${getAdditionalPaymentTranslationKeyPrefix(checkoutFlow)}.returnLink`),
        }
    }

    return getCheckoutSuccessAction({
        isPwa,
        isLoggedIn,
        locale,
        isFallback: true,
        checkoutPath,
        t,
    })
}

export function getRichCheckoutSuccessAction({
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
    hasInstallationToken,
    t,
}: {
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
    hasInstallationToken: boolean
    t: TFunction
}): CheckoutSuccessAction {
    if (isAdditionalPaymentCheckoutFlow(checkoutFlow) && returnTo && (isLoggedIn || hasInstallationToken)) {
        return {
            actionHref: returnTo,
            actionLabel: t(`${getAdditionalPaymentTranslationKeyPrefix(checkoutFlow)}.returnLink`),
        }
    }

    return getCheckoutSuccessAction({
        isPwa,
        isLoggedIn,
        locale,
        isFallback: false,
        checkoutPath,
        t,
    })
}

export function getCheckoutSuccessHeroContent({
    paymentStatus,
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
    hasInstallationToken,
    sendSms,
    t,
}: {
    paymentStatus: string
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
    hasInstallationToken: boolean
    sendSms: boolean
    t: TFunction
}) {
    const action = getRichCheckoutSuccessAction({
        isPwa,
        isLoggedIn,
        locale,
        checkoutPath,
        checkoutFlow,
        returnTo,
        hasInstallationToken,
        t,
    })
    const isPendingPayment = paymentStatus === 'created'
    const translationKeyPrefix = isAdditionalPaymentCheckoutFlow(checkoutFlow)
        ? getAdditionalPaymentTranslationKeyPrefix(checkoutFlow)
        : 'success'
    const congratulationsTextKey = translationKeyPrefix === 'success' && !sendSms
        ? `${translationKeyPrefix}.congratulations.textWithoutSms`
        : `${translationKeyPrefix}.congratulations.text`

    return {
        imageAlt: t(isPendingPayment ? `${translationKeyPrefix}.pending.imageAlt` : `${translationKeyPrefix}.congratulations.imageAlt`),
        title: t(isPendingPayment ? `${translationKeyPrefix}.pending.title` : `${translationKeyPrefix}.congratulations.title`),
        text: t(isPendingPayment ? `${translationKeyPrefix}.pending.text` : congratulationsTextKey),
        actionHref: action.actionHref,
        actionLabel: action.actionLabel,
    }
}

export function getCheckoutSuccessFallbackStateContent({
    paymentStatus,
    isPwa,
    isLoggedIn,
    locale,
    checkoutPath,
    checkoutFlow,
    returnTo,
    t,
}: {
    paymentStatus: string
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    checkoutPath: string
    checkoutFlow: CheckoutFlow
    returnTo: string | null
    t: TFunction
}): CheckoutSuccessStateContent {
    const action = getFallbackCheckoutSuccessAction({
        isPwa,
        isLoggedIn,
        locale,
        checkoutPath,
        checkoutFlow,
        returnTo,
        t,
    })
    const translationKeyPrefix = isAdditionalPaymentCheckoutFlow(checkoutFlow)
        ? getAdditionalPaymentTranslationKeyPrefix(checkoutFlow)
        : 'success'

    if (paymentStatus === 'created') {
        return {
            variant: 'pending',
            title: t(`${translationKeyPrefix}.pending.title`),
            text: t(`${translationKeyPrefix}.pending.text`),
            actionHref: action.actionHref,
            actionLabel: action.actionLabel,
        }
    }

    if (paymentStatus === 'successful') {
        return {
            variant: 'successful',
            title: t(`${translationKeyPrefix}.congratulations.title`),
            text: t(`${translationKeyPrefix}.congratulations.text`),
            actionHref: action.actionHref,
            actionLabel: action.actionLabel,
        }
    }

    return {
        variant: 'failed',
        title: t(`${translationKeyPrefix}.failed.title`),
        text: t(`${translationKeyPrefix}.failed.text`),
        actionHref: action.actionHref,
        actionLabel: action.actionLabel,
    }
}
